import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Avatar,
  Divider,
  LinearProgress,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  AccessTime as TimeIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as MoneyIcon,
  TrendingUp as TrendingUpIcon,
  Today as TodayIcon,
  DateRange as WeekIcon,
  CalendarMonth as MonthIcon,
  Videocam as VideocamIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { vi } from 'date-fns/locale';
import timesheetService from '../../services/timesheetService';

const WORK_TYPES = [
  { value: 'quay', label: '🎬 Quay phim', color: '#3b82f6' },
  { value: 'dung', label: '🎞️ Dựng video', color: '#10b981' },
  { value: 'quay_dung', label: '📹 Quay + Dựng', color: '#8b5cf6' },
  { value: 'khac', label: '📋 Khác', color: '#64748b' },
];

// Generate months array
const MONTHS = [
  { value: 1, label: 'Tháng 1' },
  { value: 2, label: 'Tháng 2' },
  { value: 3, label: 'Tháng 3' },
  { value: 4, label: 'Tháng 4' },
  { value: 5, label: 'Tháng 5' },
  { value: 6, label: 'Tháng 6' },
  { value: 7, label: 'Tháng 7' },
  { value: 8, label: 'Tháng 8' },
  { value: 9, label: 'Tháng 9' },
  { value: 10, label: 'Tháng 10' },
  { value: 11, label: 'Tháng 11' },
  { value: 12, label: 'Tháng 12' },
];

// Generate years array (from 2024 to current year)
const generateYears = () => {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let y = 2024; y <= currentYear; y++) {
    years.push(y);
  }
  return years;
};

const TimesheetManager = () => {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [history, setHistory] = useState({ entries: [], summary: {} });
  const [filter, setFilter] = useState('month');
  
  // Month/Year selection for history
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [useMonthFilter, setUseMonthFilter] = useState(false);

  // Modal states
  const [modalOpen, setModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    workDate: format(new Date(), 'yyyy-MM-dd'),
    hours: 0,
    minutes: 0,
    workType: 'quay_dung',
    description: '',
  });
  const [editingId, setEditingId] = useState(null);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [summaryResult, historyResult] = await Promise.all([
        timesheetService.getSummary(selectedMonth, selectedYear),
        useMonthFilter 
          ? timesheetService.getHistory('month', selectedMonth, selectedYear)
          : timesheetService.getHistory(filter),
      ]);

      if (summaryResult.success) setSummary(summaryResult.data);
      if (historyResult.success) setHistory(historyResult.data);
    } catch (error) {
      showSnackbar('Lỗi khi tải dữ liệu', 'error');
    } finally {
      setLoading(false);
    }
  }, [filter, selectedMonth, selectedYear, useMonthFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleOpenCreate = () => {
    setFormData({
      workDate: format(new Date(), 'yyyy-MM-dd'),
      hours: 0,
      minutes: 0,
      workType: 'quay_dung',
      description: '',
    });
    setEditingId(null);
    setModalOpen(true);
  };

  const handleOpenEdit = (entry) => {
    setFormData({
      workDate: format(new Date(entry.workDate), 'yyyy-MM-dd'),
      hours: entry.hours,
      minutes: entry.minutes,
      workType: entry.workType,
      description: entry.description || '',
    });
    setEditingId(entry._id);
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    if (formData.hours === 0 && formData.minutes === 0) {
      showSnackbar('Vui lòng nhập thời gian làm việc', 'error');
      return;
    }

    try {
      if (editingId) {
        await timesheetService.update(editingId, formData);
        showSnackbar('Cập nhật thành công!');
      } else {
        await timesheetService.create(formData);
        showSnackbar('Chấm công thành công!');
      }
      setModalOpen(false);
      fetchData();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Lỗi khi lưu', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bạn có chắc muốn xóa bản ghi này?')) return;
    try {
      await timesheetService.delete(id);
      showSnackbar('Đã xóa!');
      fetchData();
    } catch (error) {
      showSnackbar('Lỗi khi xóa', 'error');
    }
  };

  const getWorkTypeInfo = (type) => {
    return WORK_TYPES.find(w => w.value === type) || WORK_TYPES[3];
  };

  const currentMonth = summary?.current || {};
  const prevMonth = summary?.previous || {};

  if (loading && !history.entries.length) {
    return <LinearProgress />;
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b' }}>
            ⏰ Chấm công
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Quản lý thời gian làm việc & tính lương
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton onClick={fetchData}>
            <RefreshIcon />
          </IconButton>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenCreate}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': { background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)' },
            }}
          >
            Chấm công
          </Button>
        </Box>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={6} md={3}>
          <Card sx={{ borderRadius: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}>
                  <TimeIcon />
                </Avatar>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 800 }}>
                {timesheetService.formatTime(currentMonth.totalMinutes || 0)}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Tổng giờ tháng {currentMonth.month || new Date().getMonth() + 1}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ borderRadius: 3, background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}>
                  <MoneyIcon />
                </Avatar>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 800 }}>
                {timesheetService.formatCurrency(currentMonth.totalEarnings || 0)}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Thu nhập tháng này
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ borderRadius: 3, background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}>
                  <CalendarIcon />
                </Avatar>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 800 }}>
                {currentMonth.totalEntries || 0}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Số lần chấm công
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ borderRadius: 3, background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}>
                  <TrendingUpIcon />
                </Avatar>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 800 }}>
                {timesheetService.formatCurrency(100000)}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Đơn giá / giờ
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filter & History */}
      <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <Box sx={{ p: 2, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            📋 Lịch sử chấm công
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            {/* Month/Year selectors */}
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Tháng</InputLabel>
              <Select
                value={selectedMonth}
                onChange={(e) => {
                  setSelectedMonth(e.target.value);
                  setUseMonthFilter(true);
                }}
                label="Tháng"
              >
                {MONTHS.map((m) => (
                  <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl size="small" sx={{ minWidth: 100 }}>
              <InputLabel>Năm</InputLabel>
              <Select
                value={selectedYear}
                onChange={(e) => {
                  setSelectedYear(e.target.value);
                  setUseMonthFilter(true);
                }}
                label="Năm"
              >
                {generateYears().map((y) => (
                  <MenuItem key={y} value={y}>{y}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

            {/* Quick filters */}
            <ToggleButtonGroup
              value={useMonthFilter ? null : filter}
              exclusive
              onChange={(e, v) => {
                if (v) {
                  setFilter(v);
                  setUseMonthFilter(false);
                }
              }}
              size="small"
            >
              <ToggleButton value="today">
                <TodayIcon sx={{ mr: 0.5, fontSize: 18 }} /> Hôm nay
              </ToggleButton>
              <ToggleButton value="week">
                <WeekIcon sx={{ mr: 0.5, fontSize: 18 }} /> Tuần
              </ToggleButton>
              <ToggleButton value="month">
                <MonthIcon sx={{ mr: 0.5, fontSize: 18 }} /> Tháng này
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>

        {/* Summary for filtered period */}
        <Box sx={{ p: 2, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <Typography variant="caption" color="text.secondary">Tổng giờ</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#667eea' }}>
                {timesheetService.formatTime(history.summary?.totalMinutes || 0)}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="caption" color="text.secondary">Thu nhập</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#10b981' }}>
                {timesheetService.formatCurrency(history.summary?.totalEarnings || 0)}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="caption" color="text.secondary">Số lần chấm</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#f59e0b' }}>
                {history.summary?.totalEntries || 0}
              </Typography>
            </Grid>
          </Grid>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8fafc' }}>
                <TableCell sx={{ fontWeight: 600 }}>Ngày</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Thời gian</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Loại công việc</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Mô tả</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Thu nhập</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {history.entries.map((entry) => {
                const workType = getWorkTypeInfo(entry.workType);
                return (
                  <TableRow key={entry._id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CalendarIcon sx={{ fontSize: 18, color: '#64748b' }} />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {format(new Date(entry.workDate), 'dd/MM/yyyy', { locale: vi })}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {format(new Date(entry.workDate), 'EEEE', { locale: vi })}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={<TimeIcon sx={{ fontSize: 16 }} />}
                        label={timesheetService.formatTime(entry.totalMinutes)}
                        size="small"
                        sx={{ fontWeight: 600, bgcolor: '#667eea20', color: '#667eea' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={workType.label}
                        size="small"
                        sx={{ bgcolor: `${workType.color}20`, color: workType.color, fontWeight: 500 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ maxWidth: 200 }} noWrap>
                        {entry.description || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#10b981' }}>
                        {timesheetService.formatCurrency(entry.earnings)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Sửa">
                        <IconButton size="small" onClick={() => handleOpenEdit(entry)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Xóa">
                        <IconButton size="small" color="error" onClick={() => handleDelete(entry._id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
              {history.entries.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}>
                    <Typography color="text.secondary">Chưa có dữ liệu chấm công</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Create/Edit Modal */}
      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TimeIcon sx={{ color: '#667eea' }} />
            {editingId ? 'Sửa chấm công' : 'Chấm công mới'}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Ngày làm việc"
                type="date"
                value={formData.workDate}
                onChange={(e) => setFormData({ ...formData, workDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }}>
                <Chip label="⏱️ Thời gian làm việc" size="small" />
              </Divider>
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Giờ"
                type="number"
                value={formData.hours}
                onChange={(e) => setFormData({ ...formData, hours: Math.max(0, Math.min(24, parseInt(e.target.value) || 0)) })}
                InputProps={{
                  inputProps: { min: 0, max: 24 },
                  endAdornment: <Typography color="text.secondary">giờ</Typography>,
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Phút"
                type="number"
                value={formData.minutes}
                onChange={(e) => setFormData({ ...formData, minutes: Math.max(0, Math.min(59, parseInt(e.target.value) || 0)) })}
                InputProps={{
                  inputProps: { min: 0, max: 59 },
                  endAdornment: <Typography color="text.secondary">phút</Typography>,
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Paper sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">Thành tiền</Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#10b981' }}>
                  {timesheetService.formatCurrency(
                    Math.round(((formData.hours || 0) + (formData.minutes || 0) / 60) * 100000)
                  )}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  ({formData.hours || 0}h {formData.minutes || 0}p × 100,000đ/h)
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Loại công việc</InputLabel>
                <Select
                  value={formData.workType}
                  onChange={(e) => setFormData({ ...formData, workType: e.target.value })}
                  label="Loại công việc"
                >
                  {WORK_TYPES.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Mô tả công việc (tùy chọn)"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                multiline
                rows={2}
                placeholder="VD: Quay video sản phẩm mới..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setModalOpen(false)}>Hủy</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': { background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)' },
            }}
          >
            {editingId ? 'Cập nhật' : 'Chấm công'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default TimesheetManager;
